/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpenCheck,
  Bot,
  CandlestickChart,
  CheckCircle2,
  ClipboardCheck,
  Gauge,
  History,
  Layers,
  LayoutGrid,
  LineChart,
  Loader2,
  LockKeyhole,
  Mail,
  Plug,
  Search,
  SearchCheck,
  Settings2,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import PaxiaLogo from "../branding/logo";
import homepageContent from "../content/homepageContent";

const IconMap = {
  BookOpenCheck,
  Bot,
  CandlestickChart,
  ClipboardCheck,
  Gauge,
  History,
  Layers,
  LayoutGrid,
  LineChart,
  Plug,
  Search,
  SearchCheck,
  Settings2,
  ShieldAlert,
  ShieldCheck,
};

const sectionClass = "landing-section px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24";
const easeOut = [0.22, 1, 0.36, 1];

function getIcon(name, fallback = ShieldCheck) {
  return IconMap[name] || fallback;
}

function toneClass(tone) {
  switch (tone) {
    case "safe":
      return "border-emerald-300/20 bg-emerald-300/10 text-emerald-100";
    case "warning":
      return "border-amber-300/20 bg-amber-300/10 text-amber-100";
    case "blocked":
      return "border-rose-300/20 bg-rose-300/10 text-rose-100";
    default:
      return "border-sky-300/20 bg-sky-300/10 text-sky-100";
  }
}

export default function LandingPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const utm = useMemo(() => {
    if (typeof window === "undefined") {
      return { source: "", medium: "", campaign: "", content: "" };
    }

    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get("utm_source") || "",
      medium: params.get("utm_medium") || "",
      campaign: params.get("utm_campaign") || "",
      content: params.get("utm_content") || "",
    };
  }, []);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!isEmailValid) {
      setError("Please enter a valid email.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, ...utm }),
      });

      if (!response.ok) {
        throw new Error("Submission failed. Try again.");
      }

      setMessage("Thanks - you're on the list.");
      setEmail("");
    } catch (submissionError) {
      setError(submissionError.message || "Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="landing-shell min-h-screen overflow-x-hidden">
      <Hero />
      <ProblemSection />
      <WorkflowSection />
      <RiskControlsSection />
      <StudioSection />
      <DecisionLoopSection />
      <AiToolsSection />
      <AutomationSection />
      <TrustSection />
      <SignupSection
        email={email}
        error={error}
        isEmailValid={isEmailValid}
        loading={loading}
        message={message}
        onEmailChange={setEmail}
        onSubmit={handleSubmit}
      />
      <Footer />
    </main>
  );
}

function Hero() {
  const { brand, hero, navigation } = homepageContent;
  const shouldReduceMotion = useReducedMotion();
  const heroReveal = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, ease: easeOut },
      };
  const previewReveal = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        transition: { duration: 0.75, delay: 0.14, ease: easeOut },
      };

  return (
    <header className="landing-hero">
      <nav className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center gap-3" aria-label="Paxia.ai home">
          <img src={PaxiaLogo} alt="Paxia.ai" className="h-12 w-auto sm:h-[3.75rem]" />
        </a>

        <div className="hidden items-center gap-7 text-sm text-slate-300 md:flex">
          {navigation.map((item) => (
            <a key={item.href} href={item.href} className="transition-colors hover:text-[#00D5FF]">
              {item.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={hero.secondaryCta.href}
            className="hidden rounded-[8px] px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white sm:inline-flex"
          >
            {hero.secondaryCta.label}
          </Link>
          <a
            href={hero.primaryCta.href}
            className="brand-button px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00D5FF] focus:ring-offset-2 focus:ring-offset-[#05070D]"
          >
            {hero.primaryCta.label}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </nav>

      <section id="top" className="relative z-10 mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-12 sm:px-6 sm:pb-20 sm:pt-16 lg:grid-cols-[0.92fr_1.08fr] lg:px-8 lg:pb-24 lg:pt-20">
        <motion.div className="flex flex-col justify-center" {...heroReveal}>
          <p className="landing-eyebrow mb-5 px-3 py-1 text-xs font-semibold uppercase">
            {hero.eyebrow}
          </p>
          <h1 className="brand-text-gradient max-w-4xl text-5xl font-semibold leading-[1.02] sm:text-6xl lg:text-7xl">
            {hero.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
            {hero.subtitle}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href={hero.primaryCta.href}
              className="brand-button px-5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#00D5FF] focus:ring-offset-2 focus:ring-offset-[#05070D]"
            >
              {hero.primaryCta.label}
              <ArrowRight className="h-5 w-5" />
            </a>
            <Link
              to={hero.secondaryCta.href}
              className="secondary-button px-5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-[#05070D]"
            >
              {hero.secondaryCta.label}
            </Link>
          </div>

          <ul className="mt-8 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
            {hero.trustLine.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 flex-none text-[#00D5FF]" />
                {item}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div {...previewReveal}>
          <StudioPreview brand={brand} hero={hero} />
        </motion.div>
      </section>
    </header>
  );
}

function StudioPreview({ brand, hero }) {
  const previewEvents = [
    { label: "Risk rules", value: "Checked before action" },
    { label: "Trade check", value: "Within account limits" },
    { label: "Review note", value: "Ready for journal" },
    { label: "Automation", value: "Inside account rules" },
    { label: "Risk rules", value: "Checked before action" },
  ];

  return (
    <div className="relative self-center">
      <div className="soft-panel p-2 sm:p-3">
        <div className="overflow-hidden rounded-[8px] border border-white/10 bg-[#070B13]/90">
          <div className="h-1 bg-[linear-gradient(90deg,#0B3DFF,#00D5FF,#6D5BFF)]" />
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div>
              <p className="text-xs uppercase text-slate-500">{brand.category}</p>
              <p className="mt-1 font-semibold text-white">{brand.name}</p>
            </div>
            <div className="status-pill rounded-[8px] border border-[#00D5FF]/25 bg-[#00D5FF]/10 px-3 py-1.5 text-xs font-semibold text-cyan-50">
              <span className="status-dot" />
              {hero.studioPreview.account.value}
            </div>
          </div>

          <div className="px-4 pt-4">
            <div className="operation-ticker" aria-label="Live studio activity">
              <div className="operation-ticker-track">
                {previewEvents.map((event, index) => (
                  <div key={`${event.label}-${index}`} className="operation-event" aria-label={`${event.label}: ${event.value}`}>
                    <span aria-hidden="true">{event.label}</span>
                    <strong aria-hidden="true">{event.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-3 p-4 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-3">
              <Panel title={hero.studioPreview.account.label} icon={ShieldCheck}>
                <p className="text-lg font-semibold text-white">{hero.studioPreview.account.detail}</p>
                <p className="mt-2 text-sm text-slate-400">Rules are checked before new action reaches the broker.</p>
              </Panel>

              <Panel title="Guardrails" icon={LockKeyhole}>
                <div className="space-y-2">
                  {hero.studioPreview.risk.map((item) => (
                    <div key={item.label} className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.03] px-3 py-2">
                      <span className="text-sm text-slate-400">{item.label}</span>
                      <span className="text-sm font-semibold text-white">{item.value}</span>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>

            <div className="space-y-3">
              <Panel title="Chart-led trade" icon={LineChart}>
                <ChartMock />
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <MiniMetric label="Risk" value="0.5%" />
                  <MiniMetric label="R:R" value="2.1" />
                  <MiniMetric label="Status" value="Review" />
                </div>
              </Panel>

              <Panel title="Review queue" icon={ClipboardCheck}>
                <div className="space-y-2">
                  {hero.studioPreview.activity.map((item) => (
                    <div key={item.label} className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2">
                      <span className="text-sm text-slate-400">{item.label}</span>
                      <span className="text-right text-sm font-semibold text-cyan-100">{item.value}</span>
                    </div>
                  ))}
                </div>
              </Panel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProblemSection() {
  const { problem } = homepageContent;

  return (
    <Section id={problem.id} label={problem.label} title={problem.title} body={problem.body} align="left" tone="wash">
      <div className="story-grid grid gap-8 lg:grid-cols-3">
        {problem.items.map((item) => {
          const Icon = getIcon(item.icon, ShieldAlert);
          return <ProblemPoint key={item.title} icon={Icon} title={item.title} body={item.body} />;
        })}
      </div>
    </Section>
  );
}

function WorkflowSection() {
  const { workflow } = homepageContent;

  return (
    <Section id={workflow.id} label={workflow.label} title={workflow.title} body={workflow.body} tone="panel">
      <ol className="workflow-rail grid gap-3 md:grid-cols-2 lg:grid-cols-5">
        {workflow.steps.map((step, index) => {
          const Icon = getIcon(step.icon, CheckCircle2);
          return (
            <li key={step.title} className="workflow-step workflow-stage p-5" style={{ "--step-delay": `${index * 140}ms` }}>
              <div className="mb-4 flex items-center justify-between gap-4">
                <div className="icon-tile">
                  <Icon className="h-5 w-5" />
                </div>
                <span className="font-mono text-xs text-[#00D5FF]/60">{String(index + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="text-lg font-semibold text-white">{step.title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-400">{step.body}</p>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}

function RiskControlsSection() {
  const { riskControls } = homepageContent;

  return (
    <SplitSection
      id={riskControls.id}
      label={riskControls.label}
      title={riskControls.title}
      body={riskControls.body}
      visual={<RiskPanel data={riskControls} />}
      tone="wash"
    >
      <ul className="feature-rail grid gap-3 sm:grid-cols-2">
        {riskControls.features.map((feature) => (
          <li key={feature} className="feature-strand flex items-start gap-3 pr-4 text-sm text-slate-200">
            <CheckCircle2 className="h-4 w-4 flex-none text-[#00D5FF]" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <p className="safety-note mt-6 p-4 text-sm leading-6">
        {riskControls.safetyNote}
      </p>
    </SplitSection>
  );
}

function StudioSection() {
  const { studio } = homepageContent;

  return (
    <Section id={studio.id} label={studio.label} title={studio.title} body={studio.body} align="left">
      <div className="studio-stage grid gap-4 lg:grid-cols-2">
        {studio.columns.map((column, index) => {
          const Icon = getIcon(column.icon, Gauge);
          return (
            <article key={column.title} className={`studio-column p-6 ${index % 2 === 1 ? "studio-column-alt" : ""}`}>
              <div className="mb-5 flex items-center gap-3">
                <div className="icon-tile">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-semibold text-white">{column.title}</h3>
              </div>
              <ul className="studio-points space-y-3">
                {column.points.map((point) => (
                  <li key={point} className="flex gap-3 pb-3 text-sm leading-6 text-slate-300">
                    <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[#00D5FF]" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}
      </div>
    </Section>
  );
}

function DecisionLoopSection() {
  const { decisionLoop } = homepageContent;

  return (
    <Section id={decisionLoop.id} label={decisionLoop.label} title={decisionLoop.title} body={decisionLoop.body} align="left" tone="mesh">
      <div className="decision-lanes grid gap-5 lg:grid-cols-3">
        {decisionLoop.lanes.map((lane) => {
          const Icon = getIcon(lane.icon, SearchCheck);
          return <DecisionLane key={lane.title} icon={Icon} title={lane.title} body={lane.body} />;
        })}
      </div>
    </Section>
  );
}

function AiToolsSection() {
  const { aiTools } = homepageContent;
  const [leadExample, ...supportExamples] = aiTools.examples;

  return (
    <Section id={aiTools.id} label={aiTools.label} title={aiTools.title} body={aiTools.body} align="left" tone="mesh" className="landing-section-ai">
      <div className="ai-stage grid gap-10 xl:grid-cols-[0.82fr_1.18fr]">
        <div className="space-y-6">
          <div className="ai-overview p-6 sm:p-7">
            <p className="text-sm leading-7 text-slate-200">
              Describe your process in plain language, then shape the tools, data, conditions, schedules, and execution mode. It should feel like a system built around your playbook, not a generic prompt.
            </p>
            <div className="example-lead mt-6 p-5 sm:p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8BE9FF]">Example workflow</p>
              <h3 className="mt-3 text-2xl font-semibold leading-tight text-white sm:text-[1.9rem]">{leadExample.title}</h3>
              <p className="mt-4 max-w-xl text-base leading-7 text-slate-200">{leadExample.body}</p>
            </div>
            <div className="mt-4 grid gap-3">
              {supportExamples.map((example, index) => (
                <ExampleNote key={example.title} index={index + 2} title={example.title} body={example.body} />
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {aiTools.items.map((item, index) => {
          const Icon = getIcon(item.icon, Bot);
            return <AiCapability key={item.title} icon={Icon} title={item.title} body={item.body} featured={index === 0 || index === 3} />;
          })}
        </div>
      </div>

      <ResearchInputsPanel data={aiTools.researchInputs} />
      <SystemBuilderPanel data={aiTools.systemBuilder} />
    </Section>
  );
}

function ResearchInputsPanel({ data }) {
  return (
    <div className="mt-8 pt-8">
      <div className="brand-divider mb-8" />
      <div className="grid gap-8 lg:grid-cols-[0.82fr_1.18fr]">
        <div className="research-copy">
          <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">{data.label}</p>
          <h3 className="text-2xl font-semibold leading-tight text-white">{data.title}</h3>
          <p className="mt-4 text-sm leading-6 text-slate-300">{data.body}</p>
          <div className="source-cloud mt-6 flex flex-wrap gap-2.5">
            {data.sources.map((source) => (
              <span key={source} className="source-tag px-3 py-1.5 text-xs font-medium text-slate-100">
                {source}
              </span>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {data.groups.map((group) => {
            const Icon = getIcon(group.icon, SearchCheck);
            return (
              <article key={group.title} className="research-group p-5">
                <div className="icon-tile mb-3 h-9 w-9">
                  <Icon className="h-4 w-4" />
                </div>
                <h4 className="text-sm font-semibold text-white">{group.title}</h4>
                <p className="mt-2 text-sm leading-6 text-slate-400">{group.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SystemBuilderPanel({ data }) {
  return (
    <div className="builder-canvas mt-10 p-5 sm:p-6 lg:p-8">
      <div className="mb-6 max-w-3xl">
        <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">{data.label}</p>
        <h3 className="text-2xl font-semibold leading-tight text-white">{data.title}</h3>
        <p className="mt-4 text-sm leading-6 text-slate-300">{data.intro}</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="builder-prompt p-4 sm:p-5">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-cyan-50">
            <Bot className="h-4 w-4" />
            Trader prompt
          </div>
          <p className="text-sm leading-6 text-cyan-50/85">{data.prompt}</p>

          <div className="mt-5 flex flex-wrap gap-2.5">
            {data.safeguards.map((safeguard) => (
              <span key={safeguard} className="source-tag px-3 py-1.5 text-xs font-medium text-slate-100">
                {safeguard}
              </span>
            ))}
          </div>
        </div>

        <ol className="space-y-3">
          {data.steps.map((step, index) => (
            <li key={step.title} className="system-step builder-step grid grid-cols-[auto_1fr] gap-3 p-4" style={{ "--step-delay": `${index * 720}ms` }}>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0B3DFF]/20 font-mono text-xs font-semibold text-[#00D5FF]">
                {index + 1}
              </span>
              <span>
                <span className="block text-sm font-semibold text-white">{step.title}</span>
                <span className="mt-1 block text-sm leading-6 text-slate-400">{step.body}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function AutomationSection() {
  const { automation } = homepageContent;

  return (
    <SplitSection
      id={automation.id}
      label={automation.label}
      title={automation.title}
      body={automation.body}
      visual={<AutomationPanel data={automation} />}
      reverse
      tone="wash"
    >
      <p className="automation-note p-5 text-sm leading-6 text-cyan-50">
        {automation.trustCopy}
      </p>
    </SplitSection>
  );
}

function TrustSection() {
  const { trust } = homepageContent;

  return (
    <Section id={trust.id} label={trust.label} title={trust.title} body={trust.body} align="left">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {trust.points.map((point) => (
          <div key={point} className="trust-point p-4 text-sm leading-6 text-slate-300">
            <CheckCircle2 className="mb-3 h-5 w-5 text-[#00D5FF]" />
            {point}
          </div>
        ))}
      </div>
    </Section>
  );
}

function SignupSection({ email, error, isEmailValid, loading, message, onEmailChange, onSubmit }) {
  const { finalCta, hero } = homepageContent;

  return (
    <AnimatedSection id={finalCta.id} className="landing-section landing-cta px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="cta-stage p-6 sm:p-8 lg:p-10 xl:p-12">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
            <div className="relative z-10">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-[#8BE9FF]">Beta access</p>
              <h2 className="max-w-3xl text-4xl font-semibold leading-[1.04] text-white sm:text-5xl">{finalCta.title}</h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">{finalCta.body}</p>

              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {hero.trustLine.map((item) => (
                  <li key={item} className="cta-checkitem flex items-center gap-3 text-sm text-slate-100">
                    <CheckCircle2 className="h-4 w-4 flex-none text-[#00D5FF]" />
                    {item}
                  </li>
                ))}
              </ul>

              <p className="mt-6 max-w-2xl text-sm leading-6 text-slate-400">{finalCta.microcopy}</p>
            </div>

            <div className="cta-form-shell p-5 sm:p-6" aria-live="polite">
              <div className="mb-5">
                <p className="text-sm font-semibold text-white">Request beta access</p>
                <p className="mt-2 text-sm leading-6 text-slate-400">Tell us where to reach you. We will use this only for beta access updates.</p>
              </div>

              {message ? (
                <div className="cta-success p-6 text-center" role="status">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-[#00D5FF]" />
                  <p className="mt-3 text-lg font-semibold text-white">{message}</p>
                  <p className="mt-2 text-sm text-emerald-50">We received your submission and will be in touch as beta access opens.</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="grid gap-3" aria-live="polite">
                  <label className="sr-only" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(event) => onEmailChange(event.target.value)}
                    placeholder="you@domain.com"
                    className="min-h-12 rounded-[18px] border border-white/15 bg-[#05070D]/90 px-4 py-3 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-[#00D5FF] focus:ring-2 focus:ring-[#00D5FF]/20"
                  />
                  <button
                    type="submit"
                    disabled={loading || !isEmailValid}
                    className="brand-button min-h-12 px-5 py-3 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-300 disabled:shadow-none"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                    {loading ? "Submitting..." : finalCta.primaryCta.label}
                  </button>
                </form>
              )}

              {error && <p className="mt-3 text-sm text-rose-200">{error}</p>}

              <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-slate-400">
                <span>No spam. No hype. Just beta access updates.</span>
                <Link to={finalCta.secondaryCta.href} className="text-cyan-100 transition-colors hover:text-white">
                  {finalCta.secondaryCta.label}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}

function Footer() {
  const { brand, footer } = homepageContent;

  return (
    <footer className="border-t border-white/10 bg-[#05070D] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 text-sm text-slate-400 md:flex-row md:items-start md:justify-between">
        <div className="max-w-2xl">
          <img src={PaxiaLogo} alt={brand.name} className="h-10 w-auto" />
          <p className="mt-4 leading-6">{footer.disclaimer}</p>
          <p className="mt-3">© {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
        </div>
        <div className="flex flex-wrap gap-4 md:justify-end">
          {footer.links.map((link) =>
            link.href.startsWith("/") ? (
              <Link key={link.href} to={link.href} className="transition-colors hover:text-[#00D5FF]">
                {link.label}
              </Link>
            ) : (
              <a key={link.href} href={link.href} className="transition-colors hover:text-[#00D5FF]">
                {link.label}
              </a>
            ),
          )}
          <a href={`mailto:${footer.contactEmail}`} className="transition-colors hover:text-[#00D5FF]">
            {footer.contactEmail}
          </a>
        </div>
      </div>
    </footer>
  );
}

function Section({ id, label, title, body, children, tone = "default", align = "center", className = "" }) {
  const toneClassName = tone === "panel"
    ? "landing-section-panel"
    : tone === "wash"
      ? "landing-section-wash"
      : tone === "mesh"
        ? "landing-section-mesh"
        : "";

  return (
    <AnimatedSection id={id} className={`${sectionClass} ${toneClassName} ${className}`.trim()}>
      <div className="mx-auto max-w-7xl">
        <SectionIntro label={label} title={title} body={body} align={align} />
        {children}
      </div>
    </AnimatedSection>
  );
}

function SplitSection({ id, label, title, body, children, visual, reverse = false, tone = "default" }) {
  const toneClassName = tone === "panel"
    ? "landing-section-panel"
    : tone === "wash"
      ? "landing-section-wash"
      : tone === "mesh"
        ? "landing-section-mesh"
        : "";

  return (
    <AnimatedSection id={id} className={`${sectionClass} ${toneClassName}`.trim()}>
      <div className={`mx-auto grid max-w-7xl items-center gap-8 lg:grid-cols-2 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div>
          <SectionIntro label={label} title={title} body={body} align="left" />
          {children}
        </div>
        {visual}
      </div>
    </AnimatedSection>
  );
}

function AnimatedSection({ id, className, children }) {
  const shouldReduceMotion = useReducedMotion();
  const motionProps = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.12 },
        transition: { duration: 0.62, ease: easeOut },
      };

  return (
    <motion.section id={id} className={className} {...motionProps}>
      {children}
    </motion.section>
  );
}

function SectionIntro({ label, title, body, align = "center" }) {
  const isCenter = align === "center";

  return (
    <div className={`${isCenter ? "mx-auto text-center" : ""} mb-10 max-w-3xl`}>
      <p className="mb-3 text-xs font-semibold uppercase text-[#00D5FF]">{label}</p>
      <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">{title}</h2>
      <p className="mt-4 text-base leading-7 text-slate-300">{body}</p>
      {isCenter && <div className="brand-divider mx-auto mt-6 max-w-sm" />}
    </div>
  );
}

function ProblemPoint({ icon: Icon, title, body }) {
  return (
    <article className="story-card">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="icon-tile story-icon-tile">
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <h3 className="text-xl font-semibold text-white">{title}</h3>
      <p className="mt-4 max-w-sm text-sm leading-7 text-slate-300">{body}</p>
    </article>
  );
}

function DecisionLane({ icon: Icon, title, body }) {
  return (
    <article className="decision-lane p-5">
      <div className="mb-4 flex items-center gap-3">
        <div className="icon-tile h-11 w-11 rounded-full">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      <p className="max-w-sm text-sm leading-7 text-slate-300">{body}</p>
    </article>
  );
}

function AiCapability({ icon: Icon, title, body, featured = false }) {
  return (
    <article className={`ai-capability p-5 sm:p-6 ${featured ? "ai-capability-featured" : ""}`}>
      <div className="icon-tile mb-5 h-11 w-11 rounded-full">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <p className="mt-3 text-sm leading-7 text-slate-300">{body}</p>
    </article>
  );
}

function ExampleNote({ index, title, body }) {
  return (
    <article className="example-note p-4 sm:p-5">
      <div className="flex items-start gap-4">
        <span className="example-note-index flex h-9 w-9 items-center justify-center rounded-full font-mono text-xs font-semibold text-[#00D5FF]">
          {String(index).padStart(2, "0")}
        </span>
        <span>
          <span className="block text-sm font-semibold text-white">{title}</span>
          <span className="mt-2 block text-sm leading-6 text-slate-300">{body}</span>
        </span>
      </div>
    </article>
  );
}

function InfoCard({ icon: Icon, title, body }) {
  return (
    <div className="soft-card p-5">
      <div className="icon-tile mb-5">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <p className="mt-3 text-sm leading-6 text-slate-400">{body}</p>
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <div className="landing-panel p-4">
      <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-300">
        <Icon className="h-4 w-4 text-[#00D5FF]" />
        {title}
      </div>
      {children}
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="metric-tile px-2 py-2">
      <div className="text-slate-500">{label}</div>
      <div className="mt-1 font-semibold text-white">{value}</div>
    </div>
  );
}

function ChartMock() {
  const bars = [34, 46, 38, 58, 42, 66, 52, 72, 60, 78, 64, 86];

  return (
    <div className="chart-surface h-40 p-4">
      <div className="flex h-full items-end gap-2">
        {bars.map((height, index) => (
          <div key={`${height}-${index}`} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
            <div
              className={`chart-bar w-full rounded-sm ${index % 3 === 0 ? "bg-[#00D5FF]/80" : index % 3 === 1 ? "bg-[#0B3DFF]/80" : "bg-[#6D5BFF]/70"}`}
              style={{ height: `${height}%`, "--bar-delay": `${index * 90}ms` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function RiskPanel({ data }) {
  return (
    <div className="rule-sheet p-5 sm:p-6">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase text-slate-500">Account guardrails</p>
          <h3 className="mt-1 text-xl font-semibold text-white">Conservative live setup</h3>
        </div>
        <ShieldCheck className="h-7 w-7 text-[#00D5FF]" />
      </div>
      <div className="space-y-3">
        {data.exampleRules.map((rule) => (
          <div key={rule.label} className={`rule-row rounded-[18px] border px-4 py-3 ${toneClass(rule.tone)}`}>
            <div className="text-xs uppercase opacity-70">{rule.label}</div>
            <div className="mt-1 text-sm font-semibold">{rule.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AutomationPanel({ data }) {
  return (
    <div className="automation-runway p-5 sm:p-6">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase text-slate-500">Automation ladder</p>
          <h3 className="mt-1 text-xl font-semibold text-white">Gradual by design</h3>
        </div>
        <Bot className="h-7 w-7 text-[#00D5FF]" />
      </div>
      <ol className="space-y-3">
        {data.ladder.map((step, index) => (
          <li key={step.title} className="system-step automation-step grid grid-cols-[auto_1fr] gap-3 p-4" style={{ "--step-delay": `${index * 720}ms` }}>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0B3DFF]/20 font-mono text-xs text-[#00D5FF]">
              {index + 1}
            </span>
            <span>
              <span className="block text-sm font-semibold text-white">{step.title}</span>
              <span className="mt-1 block text-sm leading-6 text-slate-400">{step.body}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}